import { QueryClient } from '@tanstack/react-query';

import { ApiRegion, RETRY_FAILED_QUERIES } from '@/config';
import { queryClientDisposeOnCacheEvict } from '@/utils/queryClientDisposeOnCacheEvict';

import { isRegionalAuthError } from './regionalAuthErrors';

const clients = new Map<ApiRegion, QueryClient>();

/**
 * One QueryClient per non-Canada region. It is the namespace for every query
 * under a US RegionScope, so no query key needs a region and no US state can
 * be read by a Canada page.
 */
export const getRegionQueryClient = (region: ApiRegion): QueryClient => {
  if (region === ApiRegion.CA) {
    throw new Error('Canada uses the app QueryClient');
  }
  let regionClient = clients.get(region);
  if (!regionClient) {
    regionClient = new QueryClient({
      defaultOptions: {
        queries: {
          networkMode: 'always',
          // The client already reminted once; retrying a 401 or a denied mint only hammers the mint.
          retry: (failureCount, error) => RETRY_FAILED_QUERIES && !isRegionalAuthError(error) && failureCount < 3,
        },
      },
    });
    queryClientDisposeOnCacheEvict(regionClient);
    clients.set(region, regionClient);
  }
  return regionClient;
};

/** Retire regional clients so late callbacks cannot write into the next session. */
export const clearRegionQueryClients = () => {
  clients.forEach(regionClient => regionClient.clear());
  clients.clear();
};
